import { useController } from 'react-hook-form';
import { FormInputWithValidationProps } from '../models/FormInputWithValidation';
import { SelectProps } from '../models/SelectProps';
import { useMemo } from 'react';
import { Select } from './Select';

export const ControlledSelect = ({
  name,
  registerOptions,
  placeholder,
  options,
  defaultValue,
  emptyOption,
  mapOptions = false,
  ...rest
}: FormInputWithValidationProps &
  SelectProps &
  Partial<HTMLElement> & {
    label?: string;
    mapOptions?: boolean;
    emptyOption?: string;
  }) => {
  const {
    field: { ref, onBlur, onChange, value },
    fieldState,
    formState: { isSubmitted },
  } = useController({
    name,
    rules: registerOptions,
    defaultValue,
  });

  const wrappedOptions = useMemo(() => {
    if (mapOptions) {
      const opts = options.map((option) => ({
        label: option,
        value: String(option).toUpperCase(),
      }));
      if (emptyOption) {
        opts.unshift({
          value: null,
          label: emptyOption,
        });
      }
      return opts;
    }
    return options;
  }, [emptyOption, options, mapOptions]);

  const selectedOption = useMemo(() => {
    if (mapOptions) {
      return (
        wrappedOptions.find((option) => {
          if (!option.value) {
            return option.value === value;
          }
          return `${option.value}` === `${value}`.trim();
        }) || null
      );
    }
    return value ?? null;
  }, [wrappedOptions, value, mapOptions]);

  const handleSelection = (option: any) => {
    if (mapOptions) {
      onChange(option.value ? String(option.value).trim() : null);
    } else {
      onChange(option);
    }
  };

  return (
    <Select
      ref={ref}
      onChange={handleSelection}
      onBlur={onBlur}
      name={name}
      value={selectedOption}
      placeholder={placeholder || 'Selecciona...'}
      options={wrappedOptions}
      defaultValue={defaultValue}
      classNames={{
        control: () => {
          if (!isSubmitted) {
            return '';
          }
          return fieldState.error ? 'state-error' : 'state-valid';
        },
      }}
      {...rest}
    />
  );
};
